import { LOG_LEVELS, type ILogger, type LogLevel, type LogTransport } from './types';

const defaultConsoleTransport: LogTransport = (level, context, message, data) => {
  const consoleMap: Record<LogLevel, 'log' | 'debug' | 'info' | 'warn' | 'error'> = {
    test: 'log',
    trace: 'log',
    debug: 'debug',
    info: 'info',
    warn: 'warn',
    error: 'error',
    fatal: 'error',
    off: 'log', // never used
  };

  const tag = level.toUpperCase();
  const method = console[consoleMap[level]] as typeof console.log;
  const fullMessage = context ? `[${context}] ${message}` : message;

  if (data !== undefined) {
    method.call(console, `${tag}: ${new Date().toISOString()} - ${fullMessage}`, data);
  } else {
    method.call(console, `${tag}: ${new Date().toISOString()} - ${fullMessage}`);
  }
};

export class AppLogger implements ILogger {
  public level: LogLevel;
  private transports: Set<LogTransport>;

  constructor(level: LogLevel = 'off', transports: LogTransport[] = [defaultConsoleTransport]) {
    this.level = level;
    this.transports = new Set(transports);
  }

  setLevel(level: LogLevel) {
    this.level = level;
  }

  addTransport(t: LogTransport) {
    this.transports.add(t);
  }

  removeTransport(t: LogTransport) {
    this.transports.delete(t);
  }

  private enabled(level: LogLevel) {
    // smaller number = more verbose; block anything below current level
    if (this.level === 'off') return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[this.level];
  }

  private getCallerContext(): string {
    try {
      const stack = new Error().stack;
      if (!stack) return '';

      // Stack format:
      // Error
      //   at AppLogger.getCallerContext (...)
      //   at AppLogger.log (...)
      //   at AppLogger.info (...)
      //   at ClassName.methodName (...) <- we want this one
      const lines = stack.split('\n');

      // Find the first line that's not from AppLogger
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        if (!line.includes('AppLogger') && !line.includes('getCallerContext')) {
          // Extract the function/method name
          const match = line.match(/at\s+(?:(\S+)\.)?(\S+)\s+\(/);
          if (match) {
            const className = match[1];
            const methodName = match[2];
            return className ? `${className}.${methodName}` : methodName;
          }
        }
      }
    } catch {
      // Silently fail if stack trace parsing fails
    }
    return '';
  }

  log(level: LogLevel, message: string, data?: unknown) {
    if (!this.enabled(level) || level === 'off') return;
    const context = this.getCallerContext();
    this.transports.forEach((t) => t(level, context, message, data));
  }

  test(msg: string, data?: unknown) {
    this.log('test', msg, data);
  }
  trace(msg: string, data?: unknown) {
    this.log('trace', msg, data);
  }
  debug(msg: string, data?: unknown) {
    this.log('debug', msg, data);
  }
  info(msg: string, data?: unknown) {
    this.log('info', msg, data);
  }
  warn(msg: string, data?: unknown) {
    this.log('warn', msg, data);
  }
  error(msg: string, data?: unknown) {
    this.log('error', msg, data);
  }
  fatal(msg: string, data?: unknown) {
    this.log('fatal', msg, data);
  }
}
